import { useAuthStore } from '@/store/authStore';
import type { UserLevel } from '@/types/auth.types';
import { hasAdminAccess } from '@/lib/permissions';

/**
 * Returns the current auth state and helper methods.
 * Wraps the Zustand authStore for component consumption.
 */
export function useAuth() {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const accountId = useAuthStore((s) => s.accountId);
  const login = useAuthStore((s) => s.login);
  const logout = useAuthStore((s) => s.logout);
  const updateUser = useAuthStore((s) => s.updateUser);

  const isAuthenticated = !!token && !!user;

  const hasMinUserLevel = (level: UserLevel): boolean => {
    if (!user) return false;
    return user.userLevel >= level;
  };

  const isAdmin = hasMinUserLevel(3);

  // Super admin is the corporate `master_users.permissions.is_super_admin`
  // flag, returned by the API as `user.isSuperAdmin` on login, corp exchange
  // and /auth/me. There is deliberately no userLevel fallback: level 5 is
  // grantable by a practice's own level-5 user, so deriving from it would let
  // a practice grant itself corp-staff surfaces. A cached pre-flag session
  // reads false until the /auth/me refresh on Layout mount fills it in.
  const isSuperAdmin = user?.isSuperAdmin === true;

  // Master admin is the corporate `master_users.permissions.is_admin` flag,
  // returned by the API as `user.isMasterAdmin`. Only present on sessions
  // minted via the corp SSO bridge — no userLevel fallback, since direct
  // practice logins have no master_users identity to derive one from.
  const isMasterAdmin = !!user?.isMasterAdmin;

  const hasPermission = (permissionId: number): boolean => {
    if (!user?.permissions) return false;
    return user.permissions.includes(permissionId);
  };

  // Entry to the Admin area: level 5, or the Admin Access grant. Separate from
  // `isAdmin` on purpose — see `hasAdminAccess` in lib/permissions.
  const canAccessAdmin = !!user && hasAdminAccess(user.userLevel, user.permissions ?? []);

  return {
    token,
    user,
    accountId,
    isAuthenticated,
    isAdmin,
    hasAdminAccess: canAccessAdmin,
    isSuperAdmin,
    isMasterAdmin,
    hasMinUserLevel,
    hasPermission,
    login,
    logout,
    updateUser,
  };
}
